import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plane, MapPin, Calendar, Users, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import CoordinatorChat from "../components/CoordinatorChat";

// Simple equirectangular projection into 900x420 viewBox
function project(lng, lat) {
    const x = ((lng + 180) / 360) * 900;
    const y = ((90 - lat) / 180) * 420;
    return [x, y];
}

// Curved SVG path between two points, arcing "up"
function arcPath(x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.hypot(dx, dy);
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2 - Math.min(120, dist * 0.35);
    return `M ${x1},${y1} Q ${cx},${cy} ${x2},${y2}`;
}

export default function Transport() {
    const { user } = useAuth();
    const [legs, setLegs] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        const { data } = await api.get("/transport/legs");
        setLegs(data);
        setLoading(false);
    }
    useEffect(() => { load(); }, []);

    async function claim(leg) {
        if (!user) return toast.error("Sign in to claim a seat");
        try {
            await api.post(`/transport/legs/${leg.id}/claim`);
            toast.success(`Seat claimed on ${leg.origin} → ${leg.destination}. Coordinator will email you.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not claim seat");
        }
    }

    const selected = legs.find((l) => l.id === selectedId);

    return (
        <div data-testid="transport-page">
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                    <div className="eyebrow mb-3 inline-flex items-center gap-2"><Plane className="w-3.5 h-3.5" /> International Transport</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tight max-w-4xl">Active rescue flight legs — worldwide.</h1>
                    <p className="mt-4 text-[var(--rl-text-2)] max-w-2xl">Every leg below is a real flight route with dogs waiting for a volunteer escort or a claimed seat. Claim yours and coordinate with the on-ground team.</p>
                </div>
            </div>

            {/* Map */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                <div className="card-warm rounded-2xl overflow-hidden" data-testid="transport-map">
                    <div className="bg-[var(--rl-bg)] p-4 lg:p-8">
                        <svg viewBox="0 0 900 420" className="w-full h-auto" role="img" aria-label="Global rescue transport map">
                            {/* Subtle world grid - continent-ish blobs */}
                            <defs>
                                <radialGradient id="glow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#c45a41" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#c45a41" stopOpacity="0" />
                                </radialGradient>
                                <pattern id="dots" width="10" height="10" patternUnits="userSpaceOnUse">
                                    <circle cx="1.5" cy="1.5" r="0.9" fill="#d9dcd0" />
                                </pattern>
                            </defs>
                            <rect x="0" y="0" width="900" height="420" fill="url(#dots)" opacity="0.55" />

                            {/* Legs */}
                            {legs.map((leg) => {
                                const [x1, y1] = project(leg.origin_coords[0], leg.origin_coords[1]);
                                const [x2, y2] = project(leg.dest_coords[0], leg.dest_coords[1]);
                                const active = leg.id === selectedId;
                                const pct = leg.claimed_seats / leg.seats;
                                const urgent = pct >= 0.7;
                                return (
                                    <g key={leg.id} onClick={() => setSelectedId(leg.id)} className="cursor-pointer" data-testid={`map-leg-${leg.id}`}>
                                        <path d={arcPath(x1, y1, x2, y2)} fill="none"
                                              stroke={active ? "#c45a41" : urgent ? "#dc2626" : "#8b8878"}
                                              strokeWidth={active ? 2.4 : 1.4}
                                              strokeDasharray={active ? "0" : "3,3"}
                                              opacity={active ? 1 : 0.75} />
                                        <circle cx={x1} cy={y1} r="24" fill="url(#glow)" opacity={active ? 1 : 0} />
                                        <circle cx={x1} cy={y1} r="4" fill={active ? "#c45a41" : "#2b2b2b"} />
                                        <circle cx={x2} cy={y2} r="4" fill={active ? "#c45a41" : "#2b2b2b"} />
                                        {active && (
                                            <>
                                                <text x={x1 + 8} y={y1 - 8} fontSize="10" fontFamily="Manrope" fontWeight="600" fill="#2b2b2b">{leg.origin.split(",")[0]}</text>
                                                <text x={x2 + 8} y={y2 - 8} fontSize="10" fontFamily="Manrope" fontWeight="600" fill="#2b2b2b">{leg.destination.split(",")[0]}</text>
                                            </>
                                        )}
                                    </g>
                                );
                            })}
                        </svg>
                        <div className="mt-4 flex items-center gap-4 text-xs text-[var(--rl-text-3)]">
                            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[var(--rl-urgent)]"></span> Nearly full</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[var(--rl-text-3)]"></span> Seats available</span>
                            <span className="ml-auto">Click a route to see details</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Selected leg detail */}
            {selected && (
                <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-4">
                    <div className="card-warm rounded-2xl p-6 flex flex-wrap items-start gap-6" data-testid="selected-leg">
                        <div className="flex-1 min-w-[260px]">
                            <div className="eyebrow text-[var(--rl-primary)]">Selected route</div>
                            <div className="font-serif text-3xl mt-1 flex items-center gap-3 flex-wrap">
                                {selected.origin} <ArrowRight className="w-5 h-5 text-[var(--rl-text-3)]" /> {selected.destination}
                            </div>
                            <div className="text-sm text-[var(--rl-text-2)] mt-2">{selected.org_name}</div>
                            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[var(--rl-text-3)]" /> {fmtDate(selected.depart_date)}</span>
                                <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-[var(--rl-text-3)]" /> {selected.claimed_seats}/{selected.seats} seats claimed</span>
                            </div>
                            <p className="text-sm text-[var(--rl-text-2)] mt-3">{selected.notes}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                                {selected.dogs?.map((d, i) => <Badge key={i} variant="outline" className="border-[var(--rl-border-strong)]">{d}</Badge>)}
                            </div>
                        </div>
                        <Button className="btn-primary rounded-full h-11 px-6" onClick={() => claim(selected)}
                                disabled={selected.claimed_seats >= selected.seats}
                                data-testid="claim-seat-btn">
                            {selected.claimed_seats >= selected.seats ? "Fully claimed" : "Claim a seat"}
                        </Button>
                    </div>
                    <div className="mt-6">
                        <CoordinatorChat legId={selected.id}
                                         canPost={!!user && (selected.claimers || []).some((c) => c.user_id === user.id)} />
                    </div>
                </section>
            )}

            {/* Legs list */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
                <h2 className="font-serif text-3xl mb-6">All active routes</h2>
                {loading ? (
                    <div className="py-10 text-center text-[var(--rl-text-2)]">Loading routes…</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {legs.map((leg) => {
                            const pct = Math.round((leg.claimed_seats / leg.seats) * 100);
                            const nearlyFull = pct >= 70;
                            return (
                                <div key={leg.id} onClick={() => setSelectedId(leg.id)}
                                     className={`card-warm rounded-xl p-5 cursor-pointer ${selectedId === leg.id ? "ring-2 ring-[var(--rl-primary)]" : ""}`}
                                     data-testid={`leg-card-${leg.id}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="font-serif text-lg">{leg.origin_country}</div>
                                        <ArrowRight className="w-4 h-4 text-[var(--rl-text-3)]" />
                                        <div className="font-serif text-lg">{leg.dest_country}</div>
                                        {nearlyFull && <Badge className="bg-[var(--rl-urgent)] text-white hover:bg-[var(--rl-urgent)] text-[10px] uppercase tracking-widest ml-auto">Almost full</Badge>}
                                    </div>
                                    <div className="text-xs text-[var(--rl-text-3)] flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3" /> {leg.origin} → {leg.destination}
                                    </div>
                                    <div className="text-sm text-[var(--rl-text-2)] mt-2">{leg.org_name}</div>
                                    <div className="mt-3 flex items-center justify-between text-xs">
                                        <span className="inline-flex items-center gap-1.5 text-[var(--rl-text-3)]">
                                            <Calendar className="w-3 h-3" /> {fmtDate(leg.depart_date)}
                                        </span>
                                        <span className="text-[var(--rl-text-2)]">{leg.claimed_seats}/{leg.seats} seats</span>
                                    </div>
                                    <div className="mt-2 h-1 rounded-full bg-[var(--rl-cream)] overflow-hidden">
                                        <div className={`h-full ${nearlyFull ? "bg-[var(--rl-urgent)]" : "bg-[var(--rl-success)]"}`} style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
